import type {
  EffectivePaymentScheduleStage,
  PaymentScheduleGrant,
  PaymentScheduleSyncInvoice,
} from '@payaca/types/payment-schedule';
import { roundHalfAwayFromZero } from '@payaca/utilities/mathUtilities';
import { z } from 'zod/v4';
import {
  getStoredPercentageDue,
  isFixedDepositStage,
} from './storedPaymentSchedule';
import {
  type StoredDepositBasis,
  type StoredPaymentSchedule,
  type StoredPaymentScheduleStage,
  storedPaymentScheduleStageSchema,
} from '@payaca/types/payment-schedule';

/**
 * The percentage drives the value when set (0 treated as unset), otherwise the
 * stored fixed amount is used. Minor units.
 */
export const deriveDepositValue = (args: {
  totalIncVat: number;
  depositPercentage: number | null | undefined;
  depositAmount: number | null | undefined;
}): number | null => {
  const { totalIncVat, depositPercentage, depositAmount } = args;
  const effectivePercentage = depositPercentage || null;

  return effectivePercentage !== null
    ? roundHalfAwayFromZero((totalIncVat * effectivePercentage) / 100)
    : (depositAmount ?? null);
};

/** A stored payment schedule's shape, without the rules that make it valid. */
export const storedPaymentScheduleShapeSchema = z.object({
  stages: z.array(storedPaymentScheduleStageSchema),
  explainer: z.any().optional(),
});

/**
 * A payment schedule as the system stores it. Every stored schedule is valid:
 * rows are parsed with it, and every write is checked against it.
 */
export const storedPaymentScheduleSchema =
  storedPaymentScheduleShapeSchema.superRefine(({ stages }, ctx) => {
    const addIssue = (message: string) =>
      ctx.addIssue({ code: 'custom', message, path: ['stages'] });

    if (stages.length === 0) {
      addIssue('A payment schedule needs at least one stage');
      return;
    }

    const percentages = stages.map(getStoredPercentageDue);

    // Saving corrects a total within display precision of 100%, so only a
    // total that correction cannot bring to 100% is over.
    if (
      sumPercentages(
        withExactFullAllocation({ stages }).stages.map((stage) => ({
          percentageDue: getStoredPercentageDue(stage),
        }))
      ) > 100
    ) {
      addIssue('The stages add up to more than 100%');
    }

    if (
      percentages.some(
        (percentageDue) => percentageDue !== null && !(percentageDue > 0)
      )
    ) {
      addIssue('Every stage needs a percentage above 0');
    }

    const depositStageCount = stages.filter(
      (stage) => stage.type === 'deposit'
    ).length;

    if (depositStageCount > 1) {
      addIssue('A payment schedule can have only one deposit stage');
    } else if (depositStageCount === 1 && stages[0].type !== 'deposit') {
      addIssue('The deposit must be the first stage');
    }
  }) satisfies z.ZodType<StoredPaymentSchedule>;

/** Every reason a schedule is invalid. Empty when it is valid. */
export const getPaymentScheduleInvalidReasons = (
  paymentSchedule: StoredPaymentSchedule
): string[] => {
  const result = storedPaymentScheduleSchema.safeParse(paymentSchedule);
  return result.success
    ? []
    : [...new Set(result.error.issues.map((issue) => issue.message))];
};

export const isPaymentScheduleValid = (
  paymentSchedule: StoredPaymentSchedule
): boolean => storedPaymentScheduleSchema.safeParse(paymentSchedule).success;

/**
 * A percentage stage with its share of the gross total resolved, in minor
 * units. A fixed deposit has no value until the grants are known.
 * `storedStageIndex` is the stage's position in the stored schedule, which
 * stays the same however many stages before it are dropped.
 */
export type ValuedPaymentScheduleStage =
  | {
      type: 'deposit';
      description: string;
      value: number;
      basis: Extract<StoredDepositBasis, { type: 'percentage' }>;
      storedStageIndex: number;
    }
  | {
      type: 'instalment';
      description: string;
      value: number;
      storedStageIndex: number;
    };

/**
 * `TGrantInput` is the type of the grants passed in. A grant stage keeps every
 * field of its grant, so a caller that passes grant line IDs gets them back.
 */
export type GrantPaymentScheduleStage<
  TGrantInput extends GrantStageInput = GrantStageInput,
> = TGrantInput & { type: 'grant' };

/**
 * A stage of a payment plan once its grants are applied, in minor units.
 * `TGrantInput` is the type of the grants passed in; see
 * {@link GrantPaymentScheduleStage}.
 */
export type DerivedPaymentScheduleStage<
  TGrantInput extends GrantStageInput = GrantStageInput,
> =
  | {
      type: 'deposit';
      description: string;
      value: number;
      basis: StoredDepositBasis;
      storedStageIndex: number;
    }
  | {
      type: 'instalment';
      description: string;
      value: number;
      storedStageIndex: number;
    }
  | GrantPaymentScheduleStage<TGrantInput>;

/**
 * The schedule a direct deposit request (a proposal's deposit fields, with no
 * payment schedule) stands for: one deposit stage.
 */
export const getDirectDepositPaymentSchedule = (args: {
  depositPercentage: number | null | undefined;
  depositAmount: number | null | undefined;
}): StoredPaymentSchedule => {
  const { depositPercentage, depositAmount } = args;
  return {
    stages: [
      depositPercentage
        ? {
            type: 'deposit',
            description: 'Deposit',
            basis: { type: 'percentage', percentageDue: depositPercentage },
          }
        : {
            type: 'deposit',
            description: 'Deposit',
            basis: {
              type: 'fixed',
              fixedAmount: depositAmount ?? 0,
              percentageCap: null,
            },
          },
    ],
  };
};

/**
 * The final stage of a full schedule absorbs rounding so the values sum exactly.
 * Does not validate the schedule — guard with {@link isPaymentScheduleValid}.
 * Returns one stage per percentage stage, in order. A fixed deposit has no
 * value until the grants are known, so it is left out.
 */
const getValuedPaymentScheduleStages = (
  paymentSchedule: StoredPaymentSchedule,
  totalValue: number
): ValuedPaymentScheduleStage[] => {
  let valueRemaining = totalValue;
  const array: ValuedPaymentScheduleStage[] = [];
  let totalPercentageDue = 0;

  paymentSchedule.stages.forEach((stage, storedStageIndex) => {
    if (isFixedDepositStage(stage)) return;
    const percentageDue =
      stage.type === 'instalment'
        ? stage.percentageDue
        : stage.basis.percentageDue;

    let value = Math.round((totalValue * percentageDue) / 100);

    totalPercentageDue += percentageDue;
    // Round off float artefacts (e.g. thirds summing to 99.999…) so a full
    // schedule still hits the exact-remainder branch, matching how
    // getAllocatedPercentage treats the running total.
    const cumulativePercentageDue = Number(
      totalPercentageDue.toFixed(PERCENTAGE_PRECISION)
    );

    if (cumulativePercentageDue < 100) {
      value = Math.min(value, valueRemaining);
    } else if (cumulativePercentageDue === 100) {
      value = valueRemaining;
    }

    array.push(
      stage.type === 'instalment'
        ? {
            type: 'instalment',
            description: stage.description,
            value,
            storedStageIndex,
          }
        : {
            type: 'deposit',
            description: stage.description,
            value,
            basis: stage.basis,
            storedStageIndex,
          }
    );
    valueRemaining -= value;
  });

  return array;
};

/** A grant to overlay onto a payment schedule as a fixed-amount stage (minor units). */
export type GrantStageInput = {
  description: string;
  value: number;
};

/**
 * Overlay grants onto valued stages, each grant becoming a fixed-amount stage
 * appended at the end.
 *
 * Stages fill the total from the start (deposit first); a grant eats into it from
 * the end. So a grant absorbs any unallocated balance first, and only bites into
 * the stages, working back from the last, once that balance is consumed. A stage
 * a grant drives to zero is omitted and its remainder rolls back to the stage
 * before it. A stage that was already zero is kept, so a zero-total schedule
 * still lines up with the invoices raised against it.
 *
 * The result carries no hidden state — the gross percentages live only in the
 * stored schedule, so re-running this after the total increases re-reveals a
 * previously omitted stage.
 */
export const applyGrantStages = <TGrantInput extends GrantStageInput>(
  stages: ValuedPaymentScheduleStage[],
  grants: TGrantInput[],
  projectValue: number
): (ValuedPaymentScheduleStage | GrantPaymentScheduleStage<TGrantInput>)[] => {
  const nonGrantStages = stages.map((stage) => ({
    stage: { ...stage },
    isConsumed: false,
  }));

  const grantTotal = grants.reduce((sum, grant) => sum + grant.value, 0);
  const allocated = stages.reduce((sum, stage) => sum + stage.value, 0);
  const unallocatedBalance = Math.max(0, projectValue - allocated);

  // The grant absorbs the unallocated balance first; only the overflow reduces
  // the stages themselves, working back from the last.
  let remaining = Math.max(0, grantTotal - unallocatedBalance);
  for (let i = nonGrantStages.length - 1; i >= 0 && remaining > 0; i--) {
    const entry = nonGrantStages[i];
    const deduct = Math.min(entry.stage.value, remaining);
    if (deduct <= 0) continue;
    entry.stage.value -= deduct;
    remaining -= deduct;
    entry.isConsumed = entry.stage.value === 0;
  }

  return [
    ...nonGrantStages.flatMap((entry) =>
      entry.isConsumed ? [] : [entry.stage]
    ),
    ...grants.map((grant) => ({ ...grant, type: 'grant' as const })),
  ];
};

/**
 * The stages a payment schedule asks the customer to pay, with the grants
 * overlaid. Each grant stage carries its input grant's fields.
 */
export const getDerivedPaymentScheduleStages = <
  TGrantInput extends GrantStageInput,
>(input: {
  paymentSchedule: StoredPaymentSchedule;
  grossTotal: number;
  grants: TGrantInput[];
  /** Null values the schedule as if no deposit had been invoiced. */
  depositInvoice: { value: number } | null;
}): DerivedPaymentScheduleStage<TGrantInput>[] => {
  const { paymentSchedule, grossTotal, grants, depositInvoice } = input;

  const granted = applyGrantStages(
    getValuedPaymentScheduleStages(paymentSchedule, grossTotal),
    grants,
    grossTotal
  );
  const stages = granted.filter(
    (stage): stage is ValuedPaymentScheduleStage => stage.type !== 'grant'
  );
  const grantStages = granted.filter(
    (stage): stage is GrantPaymentScheduleStage<TGrantInput> =>
      stage.type === 'grant'
  );

  const fixedDeposit = getFixedDeposit(paymentSchedule);
  if (!fixedDeposit) return [...stages, ...grantStages];

  return [
    ...takeFixedDeposit({
      fixedDeposit,
      depositInvoice,
      stagesAfterDeposit: stages.map(assertInstalment),
      totals: {
        gross: grossTotal,
        payable: Math.max(0, grossTotal - sumValues(grantStages)),
      },
    }),
    ...grantStages,
  ];
};

/**
 * Takes a fixed deposit once the grants have been overlaid. The deposit is the
 * smallest of its fixed amount, its cap and the total payable, or once invoiced,
 * the smaller of its invoice and the total payable. It eats what the grants
 * left of the balance, then the stages from the first one forwards.
 */
const takeFixedDeposit = (input: {
  fixedDeposit: NonNullable<ReturnType<typeof getFixedDeposit>>;
  depositInvoice: { value: number } | null;
  stagesAfterDeposit: InstalmentStage[];
  totals: { gross: number; payable: number };
}): Exclude<DerivedPaymentScheduleStage, { type: 'grant' }>[] => {
  const { fixedDeposit, depositInvoice, stagesAfterDeposit, totals } = input;
  const { fixedAmount, percentageCap } = fixedDeposit.basis;

  const cap =
    percentageCap === null
      ? Infinity
      : Math.max(
          0,
          roundHalfAwayFromZero((totals.gross * percentageCap) / 100)
        );
  const amount = depositInvoice ? depositInvoice.value : fixedAmount;
  const depositValue = Math.min(
    depositInvoice ? depositInvoice.value : Math.min(fixedAmount, cap),
    totals.payable
  );
  const balance = Math.max(0, totals.payable - sumValues(stagesAfterDeposit));

  let remaining = Math.max(0, depositValue - balance);
  const reducedStages = stagesAfterDeposit.flatMap((stage) => {
    const take = Math.min(stage.value, remaining);
    remaining -= take;
    if (take > 0 && stage.value === take) return [];
    return [{ ...stage, value: stage.value - take }];
  });

  if (depositValue === 0 && amount > 0) return reducedStages;
  return [
    {
      type: 'deposit',
      description: fixedDeposit.description,
      value: depositValue,
      basis: fixedDeposit.basis,
      storedStageIndex: 0,
    },
    ...reducedStages,
  ];
};

type InstalmentStage = Extract<
  DerivedPaymentScheduleStage,
  { type: 'instalment' }
>;

const assertInstalment = (
  stage: Exclude<DerivedPaymentScheduleStage, { type: 'grant' }>
): InstalmentStage => {
  if (stage.type !== 'instalment') {
    throw new Error('A payment schedule has a second deposit stage');
  }
  return stage;
};

const sumValues = (stages: { value: number }[]) =>
  stages.reduce((sum, stage) => sum + stage.value, 0);

const getFixedDeposit = (paymentSchedule: StoredPaymentSchedule) => {
  const [first] = paymentSchedule.stages;
  return first && isFixedDepositStage(first) ? first : null;
};

/**
 * Each stored stage with its value before grants, for readers that show the
 * schedule as it was set up.
 */
export const getStoredStagesWithGrossValues = (
  paymentSchedule: StoredPaymentSchedule,
  totalValue: number
): { stage: StoredPaymentScheduleStage; value: number | null }[] => {
  const values = getValuedPaymentScheduleStages(paymentSchedule, totalValue);
  return paymentSchedule.stages.map((stage, i) => ({
    stage,
    value:
      values.find((valued) => valued.storedStageIndex === i)?.value ?? null,
  }));
};

export const getShareOfTotal = (value: number, grossTotal: number): number =>
  grossTotal > 0 ? (value / grossTotal) * 100 : 0;

/**
 * A derived stage in the shape the client portal and Provider App receive. A
 * grant and a fixed deposit show an amount only, so have no percentage.
 */
export const toEffectivePaymentScheduleStage = (
  stage: DerivedPaymentScheduleStage,
  grossTotal: number
): Omit<EffectivePaymentScheduleStage, 'isPaid'> => ({
  description: stage.description,
  percentageDue:
    stage.type === 'grant' ||
    (stage.type === 'deposit' && stage.basis.type === 'fixed')
      ? null
      : getShareOfTotal(stage.value, grossTotal),
  isDeposit: stage.type === 'deposit',
  isGrant: stage.type === 'grant',
  value: stage.value,
});

type PaymentScheduleSyncPairing<TInvoice> = {
  invoice: TInvoice;
  invoiceValue: number | null;
  invoiceProjectPercentage: number;
};

/**
 * A row of the sync result: a schedule stage or a grant, each paired with the
 * invoice raised for it, or an invoice that no stage or grant accounts for.
 */
export type PaymentScheduleSyncStage<
  TInvoice extends PaymentScheduleSyncInvoice,
> =
  | ({
      type: 'stage';
      stage: Exclude<
        DerivedPaymentScheduleStage<PaymentScheduleGrant>,
        { type: 'grant' }
      >;
    } & PaymentScheduleSyncPairing<TInvoice | null>)
  | ({
      type: 'grant';
      stage: GrantPaymentScheduleStage<PaymentScheduleGrant>;
    } & PaymentScheduleSyncPairing<TInvoice | null>)
  | ({
      type: 'unmatchedInvoice';
      stage: null;
    } & PaymentScheduleSyncPairing<TInvoice>)
  | ({
      type: 'unmatchedGrantInvoice';
      grantLineId: number | null;
      stage: null;
    } & PaymentScheduleSyncPairing<TInvoice>);

export type PaymentScheduleSyncResult<
  TInvoice extends PaymentScheduleSyncInvoice,
> = {
  isInSync: boolean;
  stages: PaymentScheduleSyncStage<TInvoice>[];
};

/**
 * Whether a change proposal's payment schedule must be rebuilt from its
 * invoices: it is out of sync, and an invoice paired with its stages isn't on a
 * stage of the same kind (a deposit invoice on the deposit stage, any other
 * invoice on another stage), or has no stage at all. Grant invoices pair with
 * grant stages, so they never appear in the rows.
 */
export const isPaymentScheduleRebuildRequired = (args: {
  isInSync: boolean;
  rows: {
    stage: { type: 'deposit' | 'instalment' } | null;
    invoice?: { isDepositInvoice: boolean | null } | null;
  }[];
}): boolean => !args.isInSync && !doInvoicesMatchStages(args.rows);

export const isFixedDepositConvertible = (args: {
  isInSync: boolean;
  rows: {
    stage: StoredPaymentScheduleStage | null;
    invoice?: { isDepositInvoice: boolean | null } | null;
  }[];
}): boolean => {
  const [deposit, ...rest] = args.rows;
  return (
    !args.isInSync &&
    !isPaymentScheduleRebuildRequired(args) &&
    !!deposit?.stage &&
    isFixedDepositStage(deposit.stage) &&
    !!deposit.invoice?.isDepositInvoice &&
    rest.some(
      ({ stage, invoice }) =>
        stage?.type === 'instalment' && invoice && !invoice.isDepositInvoice
    )
  );
};

const doInvoicesMatchStages = (
  rows: Parameters<typeof isPaymentScheduleRebuildRequired>[0]['rows']
): boolean =>
  rows.every(
    ({ stage, invoice }) =>
      !invoice ||
      (!!stage && (stage.type === 'deposit') === !!invoice.isDepositInvoice)
  );

export const getPaymentScheduleSyncResult = <
  TInvoice extends PaymentScheduleSyncInvoice,
>(
  paymentSchedule: StoredPaymentSchedule,
  projectValue: number,
  invoices: TInvoice[],
  grants: PaymentScheduleGrant[] = []
): PaymentScheduleSyncResult<TInvoice> => {
  let isInSync = true;

  const depositInvoices = invoices.filter(
    (invoice) => invoice.isDepositInvoice && !invoice.isGrantInvoice
  );
  if (depositInvoices.length > 1) {
    isInSync = false;
  }
  const depositInvoice =
    depositInvoices.length === 1
      ? { value: depositInvoices[0]?.totalValue ?? 0 }
      : null;

  const invoiceProjectPercentage = (invoice: TInvoice | null) =>
    invoice && projectValue
      ? ((invoice.totalValue || 0) / projectValue) * 100
      : 0;

  const derivedStages = getDerivedPaymentScheduleStages({
    paymentSchedule,
    grossTotal: projectValue,
    grants,
    depositInvoice,
  });
  const reducedNonGrantStages = derivedStages.filter(
    (stage) => stage.type !== 'grant'
  );
  const grantStages = derivedStages.filter((stage) => stage.type === 'grant');

  // Grant invoices reconcile only against grant stages, never against non-grant
  // stages, so keep them out of the positional pairing.
  const nonGrantInvoices = invoices.filter(
    (invoice) => !invoice.isGrantInvoice
  );
  const grantInvoices = invoices.filter((invoice) => invoice.isGrantInvoice);

  // A fixed deposit can use up the balance even when the stages after it add
  // up to less than 100%, so check the amounts rather than the percentages.
  const isBalanceUsedUp =
    getFixedDeposit(paymentSchedule) !== null &&
    Math.max(0, projectValue - sumValues(grants)) <=
      sumValues(reducedNonGrantStages);
  const totalPercentage = paymentSchedule.stages.reduce(
    (acc, stage) => acc + (getStoredPercentageDue(stage) ?? 0),
    0
  );

  if (
    nonGrantInvoices.length > reducedNonGrantStages.length &&
    (totalPercentage === 100 || isBalanceUsedUp)
  ) {
    isInSync = false;
  }

  const stages: PaymentScheduleSyncResult<TInvoice>['stages'] = [];

  const loopLength = Math.max(
    nonGrantInvoices.length,
    reducedNonGrantStages.length
  );
  for (let i = 0; i < loopLength; i++) {
    const invoice = nonGrantInvoices.length > i ? nonGrantInvoices[i] : null;
    const stage =
      reducedNonGrantStages.length > i ? reducedNonGrantStages[i] : null;

    if (!stage) {
      if (!invoice) continue;
      stages.push({
        type: 'unmatchedInvoice',
        stage: null,
        invoice,
        invoiceValue: invoice?.totalValue ?? null,
        invoiceProjectPercentage: invoiceProjectPercentage(invoice),
      });
      continue;
    }

    if (invoice) {
      if ((stage.type === 'deposit') !== !!invoice.isDepositInvoice) {
        isInSync = false;
      }
      if (invoice.totalValue !== stage.value) {
        isInSync = false;
      }
    }

    stages.push({
      stage,
      type: 'stage',
      invoice: invoice || null,
      invoiceValue: invoice?.totalValue ?? null,
      invoiceProjectPercentage: invoiceProjectPercentage(invoice),
    });
  }

  const matchedGrantInvoices = new Set<TInvoice>();
  for (const grant of grantStages) {
    const invoice =
      grantInvoices.find(
        (candidate) =>
          candidate.grantLineId === grant.grantLineId &&
          !matchedGrantInvoices.has(candidate)
      ) ?? null;
    if (invoice) {
      matchedGrantInvoices.add(invoice);
      if (invoice.totalValue !== grant.value) {
        isInSync = false;
      }
    }

    stages.push({
      stage: grant,
      type: 'grant',
      invoice,
      invoiceValue: invoice?.totalValue ?? null,
      invoiceProjectPercentage: invoiceProjectPercentage(invoice),
    });
  }

  // A grant invoice with no matching grant stage (e.g. its grant was removed) is
  // out of sync and surfaced as an orphaned grant row.
  for (const invoice of grantInvoices) {
    if (matchedGrantInvoices.has(invoice)) continue;
    isInSync = false;
    stages.push({
      type: 'unmatchedGrantInvoice',
      stage: null,
      grantLineId: invoice.grantLineId ?? null,
      invoice,
      invoiceValue: invoice.totalValue ?? null,
      invoiceProjectPercentage: invoiceProjectPercentage(invoice),
    });
  }

  return { isInSync, stages };
};

/**
 * Percentage values are rounded to 5 decimal places so float artefacts from
 * amount-derived percentages can't push an exact total just past 100%.
 */
export const PERCENTAGE_PRECISION = 5;

/**
 * Editors show percentages to 2 decimal places, so a total that misses 100% by
 * less than half of 0.01% looks like 100% and is treated as 100%.
 */
const FULL_ALLOCATION_TOLERANCE = 0.005;

const roundPercentage = (percentage: number): number =>
  Number(percentage.toFixed(PERCENTAGE_PRECISION));

const sumPercentages = (stages: { percentageDue?: number | null }[]): number =>
  roundPercentage(
    stages.reduce((acc, stage) => acc + (stage.percentageDue ?? 0), 0)
  );

const isWithinTolerance = (percentage: number, target: number): boolean =>
  Math.abs(roundPercentage(percentage - target)) < FULL_ALLOCATION_TOLERANCE;

/**
 * Sum of stage percentages, rounded to {@link PERCENTAGE_PRECISION}. A sum
 * within tolerance of 100 is rounded to 100.
 */
export const getAllocatedPercentage = (
  stages: { percentageDue?: number | null }[]
): number => {
  const allocated = sumPercentages(stages);
  return isWithinTolerance(allocated, 100) ? 100 : allocated;
};

/**
 * A stage's percentage, replaced by whatever brings the schedule to exactly
 * 100% when it is within {@link FULL_ALLOCATION_TOLERANCE} of that.
 */
export const snapToRemainingPercentage = (
  percentageDue: number,
  otherStages: { percentageDue?: number | null }[]
): number => {
  const remaining = roundPercentage(100 - sumPercentages(otherStages));
  return remaining > 0 && isWithinTolerance(percentageDue, remaining)
    ? remaining
    : percentageDue;
};

/**
 * The schedule with its last percentage stage changed so the stages total
 * exactly 100%, when they are within {@link FULL_ALLOCATION_TOLERANCE} of it.
 * Any other schedule is returned as it is.
 */
export const withExactFullAllocation = (
  paymentSchedule: StoredPaymentSchedule
): StoredPaymentSchedule => {
  const percentages = paymentSchedule.stages.map((stage) => ({
    percentageDue: getStoredPercentageDue(stage),
  }));
  const lastIndex = percentages.findLastIndex(
    ({ percentageDue }) => percentageDue !== null
  );
  const lastPercentage = percentages[lastIndex]?.percentageDue;
  if (lastPercentage === null || lastPercentage === undefined) {
    return paymentSchedule;
  }

  const percentageDue = snapToRemainingPercentage(
    lastPercentage,
    percentages.filter((_, index) => index !== lastIndex)
  );
  if (percentageDue === lastPercentage) return paymentSchedule;

  return {
    ...paymentSchedule,
    stages: paymentSchedule.stages.map((stage, index) => {
      if (index !== lastIndex) return stage;
      return stage.type === 'instalment'
        ? { ...stage, percentageDue }
        : { ...stage, basis: { type: 'percentage', percentageDue } };
    }),
  };
};

/**
 * The percentage of `total` that `amount` represents, rounded to
 * {@link PERCENTAGE_PRECISION}. Every surface that lets a stage be entered as a
 * money amount must round here, or the schedule it persists can sum past 100%.
 */
export const percentageFromAmount = (amount: number, total: number): number =>
  roundPercentage((amount / total) * 100);
